import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { Switch, Typography, Space, Form, Flex, Button, Tooltip } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type FC,
  type RefObject,
} from 'react';

import { ExtendedMessage } from '@app/components';
import { PREFIX as GLOBAL_PREFIX } from '@app/constants/appPrefix';
import type { NRecordReminders } from '@app/types';
import { createId } from '@app/utils';

import { ReminderTypes } from '@pages/Notifications/constants/reminderTypes';

import { LabelWithTime } from '@pages/Notifications/components/LabelWithTime';
import { ReminderFiles } from '@pages/Notifications/components/ReminderFiles';

import type { FormInstance } from 'antd';

import '@pages/Notifications/styles/NotificationVisitReminder.desktop.scss';

const { Title, Paragraph, Text } = Typography;
const { useWatch } = Form;

const COMPONENT_CLASS_NAME = `${GLOBAL_PREFIX}notification-visit-reminder`;

type TNotificationVisitReminderProps = {
  form: FormInstance;
  visitReminderBoxRef: RefObject<HTMLDivElement>;
  notifications: (NRecordReminders.ModelRecordReminder & { frontId: string })[];
  setNotifications: (
    notifications: (NRecordReminders.ModelRecordReminder & {
      frontId: string;
    })[]
  ) => void;
  deleteNotification: (frontId: string) => () => void;
  reminder: NRecordReminders.ModelRecordReminder | null;
  disabled?: boolean;
};

const { Second } = ReminderTypes;

export const NotificationVisitReminder: FC<TNotificationVisitReminderProps> = ({
  form,
  visitReminderBoxRef,
  notifications,
  setNotifications,
  deleteNotification,
  reminder,
  disabled = false,
}) => {
  const isSyncingRef = useRef(false);

  const syncNotificationsWithForm = useCallback(() => {
    isSyncingRef.current = true;
    const updated = notifications.map((notification) => {
      const hours =
        form.getFieldValue(
          `generalReminderIntervalHours${notification.frontId}`
        ) || 0;
      const minutes =
        form.getFieldValue(
          `generalReminderIntervalMinutes${notification.frontId}`
        ) || 0;
      return {
        ...notification,
        ReminderMinutes: hours * 60 + minutes,
      };
    });
    setNotifications(updated);
    isSyncingRef.current = false;
  }, [form, notifications, setNotifications]);

  const addNotification = useCallback(() => {
    syncNotificationsWithForm();
    setNotifications([
      ...notifications,
      {
        frontId: createId(),
        Id: 0,
        IsOn: notifications?.[0]?.IsOn || false,
        ReminderType: ReminderTypes.Second,
        ReminderMinutes: 0,
        Text: notifications?.[0]?.Text || '',
        Json: notifications?.[0]?.Json || null,
      },
    ]);
  }, [notifications, setNotifications, syncNotificationsWithForm]);

  const handleDeleteNotification = useCallback(
    (frontId: string) => () => {
      syncNotificationsWithForm();
      deleteNotification(frontId)();
    },
    [deleteNotification, syncNotificationsWithForm]
  );

  const handleTimeChange = useCallback(
    (frontId: string, type: 'hours' | 'minutes', value: number) => {
      const updated = notifications.map((notification) => {
        if (notification.frontId !== frontId) return notification;
        const hours =
          type === 'hours'
            ? value
            : form.getFieldValue(`generalReminderIntervalHours${frontId}`) || 0;
        const minutes =
          type === 'minutes'
            ? value
            : form.getFieldValue(`generalReminderIntervalMinutes${frontId}`) ||
              0;
        return {
          ...notification,
          ReminderMinutes: hours * 60 + minutes,
        };
      });
      setNotifications(updated);
    },
    [form, notifications, setNotifications]
  );

  const componentNotifications = useMemo(
    () => notifications.filter(({ ReminderType }) => ReminderType === Second),
    [notifications]
  );

  useEffect(() => {
    if (isSyncingRef.current) return;
    componentNotifications.forEach(({ frontId, ReminderMinutes }) => {
      const minutes = ReminderMinutes % 60;
      const hours = Math.floor(ReminderMinutes / 60);
      form.setFieldsValue({
        [`generalReminderIntervalMinutes${frontId}`]: minutes,
        [`generalReminderIntervalHours${frontId}`]: hours,
      });
    });
  }, [componentNotifications, form]);

  const changeStatus = useCallback(
    (type: string, checked: boolean, itemName: string) => () => {
      form.setFieldsValue({
        [itemName]: checked,
      });
      setNotifications(
        notifications.map((notification) => {
          if (notification.ReminderType === type) {
            return { ...notification, IsOn: checked };
          }
          return notification;
        })
      );
    },
    [form, notifications, setNotifications]
  );

  const isGeneralReminderStatus = useWatch('generalReminderStatus', form);
  const isConfirmByWords = useWatch('confirmByWords', form);

  return (
    <div className={COMPONENT_CLASS_NAME} ref={visitReminderBoxRef}>
      <Flex justify="space-between" align="center">
        <Title
          level={4}
          className={`${COMPONENT_CLASS_NAME}__header`}
          data-testid="reminder-about-visit-block-title"
        >
          Напоминание клиенту о визите
        </Title>
        <Form.Item
          name="generalReminderStatus"
          className={`${COMPONENT_CLASS_NAME}__switch`}
        >
          <Switch
            data-testid={
              form.getFieldValue('createRecordStatus')
                ? 'active-notification'
                : 'inactive-notification'
            }
            onChange={(checked) =>
              changeStatus(Second, checked, 'generalReminderStatus')
            }
            disabled={disabled}
          />
        </Form.Item>
      </Flex>

      {isGeneralReminderStatus && (
        <>
          <Paragraph
            className={`${COMPONENT_CLASS_NAME}__description`}
            data-testid="reminder-about-visit-description"
          >
            Настройте сообщение, отправляемое клиенту в WhatsApp, заранее, перед
            предстоящей записью (со статусом "Ожидание клиента").
          </Paragraph>

          <Space direction="vertical" size="middle">
            {componentNotifications.map(({ frontId }) => {
              const hoursValue =
                form.getFieldValue(`generalReminderIntervalHours${frontId}`) ||
                0;
              const minutesValue =
                form.getFieldValue(
                  `generalReminderIntervalMinutes${frontId}`
                ) || 0;
              return (
                <Space key={frontId} size={20} align="center">
                  <LabelWithTime
                    componentClassName={COMPONENT_CLASS_NAME}
                    type="before"
                    minutesFormItemName={`generalReminderIntervalMinutes${frontId}`}
                    hoursFormItemName={`generalReminderIntervalHours${frontId}`}
                    onHoursChange={(value: number) =>
                      handleTimeChange(frontId, 'hours', value)
                    }
                    onMinutesChange={(value: number) =>
                      handleTimeChange(frontId, 'minutes', value)
                    }
                    hoursValue={hoursValue}
                    minutesValue={minutesValue}
                    withExtendedHours
                    disabled={disabled}
                  />
                  {componentNotifications.length > 1 && (
                    <Tooltip title="Удалить интервал напоминания">
                      <Button
                        icon={<DeleteOutlined />}
                        danger
                        onClick={handleDeleteNotification(frontId)}
                        disabled={disabled}
                      />
                    </Tooltip>
                  )}
                </Space>
              );
            })}
            <Button
              block
              icon={<PlusOutlined />}
              onClick={addNotification}
              data-testid="add-notification"
              disabled={disabled}
            >
              Добавить уведомление
            </Button>
          </Space>

          <Text
            className={`${COMPONENT_CLASS_NAME}__label`}
            data-testid="notification-title"
          >
            Текст уведомления
          </Text>
          <ExtendedMessage
            form={form}
            socialType="whatsapp"
            formItemName="generalReminderMessage"
            formItemClassName={`${COMPONENT_CLASS_NAME}__textarea`}
            textAreaDataTestId="notification-template-text"
            disabled={disabled}
          />

          <ReminderFiles
            reminder={reminder}
            componentClassName={COMPONENT_CLASS_NAME}
            disabled={disabled}
          />

          <Space>
            <Form.Item
              name="confirmByWords"
              className={`${COMPONENT_CLASS_NAME}__switch`}
            >
              <Switch
                data-testid={
                  form.getFieldValue('confirmByWords')
                    ? 'active-notification'
                    : 'inactive-notification'
                }
                disabled={disabled}
              />
            </Form.Item>
            <Text>Напомнить словами</Text>
          </Space>
          {isConfirmByWords && (
            <>
              <Text
                className={`${COMPONENT_CLASS_NAME}__label`}
                data-testid="notification-title"
              >
                Подтверждение записи словами
              </Text>
              <Paragraph className={`${COMPONENT_CLASS_NAME}__description`}>
                Перечислите через запятую символы и слова, которыми клиент может
                подтвердить свою запись (без учёта регистра).
              </Paragraph>
              <Form.Item name="confirmByWordsText">
                <TextArea rows={4} disabled={disabled} />
              </Form.Item>
            </>
          )}
        </>
      )}
    </div>
  );
};
